import { FC } from "react";
import { ArrowRight } from "lucide-react";
import type { WorkOrderStatus } from "@myapp/contract";
import Modal from "../../../shared/ui/overlay/Modal/Modal";
import { Badge } from "../../../shared/ui";
import {
  getWorkOrderStatusColor,
  getWorkOrderStatusLabel,
} from "../../../entities/work-order";

interface Props {
  /** The status being moved to; `null` keeps the dialog closed. */
  to: WorkOrderStatus | null;
  from: WorkOrderStatus;
  isPending: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The last look before the status is overwritten. It shows the change as it will read — old pill,
 * arrow, new pill — and says what this does *not* do, because the thing a manager worries about is
 * whether their Termine are about to move. Same shape as `DiscardChangesDialog`.
 */
export const ConfirmStatusChangeDialog: FC<Props> = ({
  to,
  from,
  isPending,
  error,
  onConfirm,
  onCancel,
}) => (
  <Modal
    empty
    show={to != null}
    setShow={(next) => {
      // A save in flight cannot be backed out of by Escape or the backdrop.
      if (!next && !isPending) onCancel();
    }}
    ariaLabel="Status ändern"
    // `!`: the Modal's own `.popUp { width: auto }` outranks a plain width utility, which left the
    // dialog as wide as its longest line of text. `min()` keeps 400px on desktop and gives way to
    // the 16px gutters on a phone.
    className="flex !w-[min(400px,calc(100vw-32px))] flex-col gap-4 p-6"
  >
    <p className="text-base font-semibold text-gray-900">Status ändern?</p>

    {to && (
      <div className="flex flex-wrap items-center gap-3" aria-label="Statuswechsel">
        <Badge color={getWorkOrderStatusColor(from)} label={getWorkOrderStatusLabel(from)} />
        <ArrowRight size={18} aria-hidden="true" className="shrink-0 text-slate-500" />
        <Badge color={getWorkOrderStatusColor(to)} label={getWorkOrderStatusLabel(to)} />
      </div>
    )}

    <p className="text-sm text-gray-600">
      Es wird nur der Status des Auftrags gesetzt. Die Termine bleiben unverändert und niemand wird
      benachrichtigt. Die Änderung steht im Verlauf.
    </p>

    {error && (
      <p className="text-sm text-red-500" role="alert">
        {error}
      </p>
    )}

    {/* Zurück first in the DOM and on the left, so Tab reaches the safe action before the one that
        writes; the action sits on the right in the brand colour, where a confirm is looked for.
        Plain buttons rather than `Button`: that one is a 55px / 25px primary, and its variant
        background would outrank any colour passed in. */}
    <div className="mt-2 flex justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={isPending}
        className="h-11 rounded-full border border-slate-300 bg-white px-5 text-base font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#124093] disabled:cursor-not-allowed disabled:opacity-60"
      >
        Zurück
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={isPending}
        aria-busy={isPending || undefined}
        className="h-11 min-w-[9.5rem] rounded-full bg-[#124093] px-6 text-base font-semibold text-white transition-colors duration-150 hover:bg-[#0e3277] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#124093] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isPending ? "Wird gespeichert…" : "Status ändern"}
      </button>
    </div>
  </Modal>
);
